import React, { useState } from 'react';
import { useTheme } from '../../context/ThemeContext';
import { ArrowDownTrayIcon, ExclamationCircleIcon, CalendarIcon, ClockIcon, ChartBarIcon } from '@heroicons/react/24/outline';
import { Customer } from '../../providers/customer/types';
import {StockServiceContext} from '../../context/stock';



interface TickerDataDownloadProps {
  customer: Customer;
}

export const TickerDataDownload: React.FC<TickerDataDownloadProps> = ({ customer }) => {
  const { isDarkMode } = useTheme();
  const [ticker, setTicker] = useState('');
  const [interval, setInterval] = useState('1d');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [isDownloading, setIsDownloading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const stockService = React.useContext(StockServiceContext);
  

  const generateAccessToken = async () => {
    if (!customer?.api_key || !customer?.api_secret) {
      throw new Error('API credentials are missing. Please check your profile settings.');
    }

    // If access token exists, return it
    if (customer.access_token) {
      return customer.access_token;
    }
    
    
    try {
      const response = await stockService.GetAccessToken(customer.api_key, customer.api_secret);
      return response.accessToken;
    } catch (error) {
      console.error('Error generating access token:', error);
      throw new Error('Failed to generate access token');
    }
  };

  const handleDownload = async () => {
    setError(null);
    setSuccess(false);
    
    if (!ticker || !startDate || !endDate) {
      setError('Please fill in all required fields');
      return;
    }

    if (!customer?.api_key || !customer?.api_secret) {
      setError('API credentials are missing. Please check your profile settings.');
      return;
    }

    setIsDownloading(true);
    try {
      const accessToken =  await generateAccessToken();
      const response = await stockService.DownloadStockData(ticker, interval, startDate, endDate, accessToken);

      const url = window.URL.createObjectURL(new Blob([response]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${ticker}_${interval}_${startDate}_to_${endDate}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      setSuccess(true);
    } catch (error) {
      console.error('Error downloading data:', error);
      setError('Error downloading data. Please try again.');
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <div className={`min-h-screen ${isDarkMode ? 'bg-gray-900' : 'bg-gray-50'} py-8 px-4 sm:px-6 lg:px-8`}>
      <div className={`max-w-4xl mx-auto ${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-3xl shadow-lg overflow-hidden transition-all duration-300 hover:shadow-xl`}>
        <div className={`p-8 ${isDarkMode ? 'bg-gray-900' : 'bg-gray-50'} rounded-t-3xl`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className={`p-3 rounded-xl ${isDarkMode ? 'bg-green-500/10' : 'bg-green-50'}`}>
                <ChartBarIcon className={`h-6 w-6 ${isDarkMode ? 'text-green-400' : 'text-green-600'}`} />
              </div>
              <div>
                <h3 className={`text-xl font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  Stock Data Download
                </h3>
                <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  Download historical market data for analysis
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="p-8">
          {error && (
            <div className="mb-6 p-4 rounded-xl bg-red-50 border border-red-200">
              <div className="flex items-center">
                <ExclamationCircleIcon className="h-5 w-5 text-red-400 mr-2" />
                <p className="text-sm text-red-600">{error}</p>
              </div>
            </div>
          )}
          {success && (
            <div className="mb-6 p-4 rounded-xl bg-green-50 border border-green-200">
              <p className="text-sm text-green-600">Download completed successfully!</p>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-6">
              <div>
                <label htmlFor="ticker" className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  ISIN Symbol
                </label>
                <div className="relative">
                  <input
                    type="text"
                    name="ticker"
                    id="ticker"
                    value={ticker}
                    onChange={(e) => setTicker(e.target.value.toUpperCase())}
                    className={`block w-full pl-10 pr-4 py-3 rounded-xl border ${isDarkMode ? 'border-gray-700 bg-gray-700 text-white' : 'border-gray-300 bg-white text-gray-900'} shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm transition-colors duration-200`}
                    placeholder="e.g., INE101I01011, INF209KB19Z4, INE228I01012"
                  />
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <span className={`${isDarkMode ? 'text-gray-400' : 'text-gray-500'} sm:text-sm`}>$</span>
                  </div>
                </div>
              </div>

              <div>
                <label htmlFor="interval" className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Time Interval
                </label>
                <div className="relative">
                  <select
                    id="interval"
                    name="interval"
                    value={interval}
                    onChange={(e) => setInterval(e.target.value)}
                    className={`block w-full pl-10 pr-4 py-3 rounded-xl border ${isDarkMode ? 'border-gray-700 bg-gray-700 text-white' : 'border-gray-300 bg-white text-gray-900'} shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm transition-colors duration-200`}
                  >
                    <option value="1m">1 Minute</option>
                    <option value="5m">5 Minutes</option>
                    <option value="15m">15 Minutes</option>
                    <option value="30m">30 Minutes</option>
                    <option value="1h">1 Hour</option>
                    <option value="1d">1 Day</option>
                    <option value="1w">1 Week</option>
                    <option value="1M">1 Month</option>
                  </select>
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <ClockIcon className={`h-5 w-5 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`} />
                  </div>
                </div>
              </div>
            </div>

            <div className="space-y-6">
              <div>
                <label htmlFor="startDate" className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Start Date
                </label>
                <div className="relative">
                  <input
                    type="date"
                    id="startDate"
                    name="startDate"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className={`block w-full pl-10 pr-4 py-3 rounded-xl border ${isDarkMode ? 'border-gray-700 bg-gray-700 text-white' : 'border-gray-300 bg-white text-gray-900'} shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm transition-colors duration-200`}
                  />
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <CalendarIcon className={`h-5 w-5 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`} />
                  </div>
                </div>
              </div>

              <div>
                <label htmlFor="endDate" className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  End Date
                </label>
                <div className="relative">
                  <input
                    type="date"
                    id="endDate"
                    name="endDate"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className={`block w-full pl-10 pr-4 py-3 rounded-xl border ${isDarkMode ? 'border-gray-700 bg-gray-700 text-white' : 'border-gray-300 bg-white text-gray-900'} shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm transition-colors duration-200`}
                  />
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <CalendarIcon className={`h-5 w-5 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`} />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-8">

            <button
              type="button"
              onClick={handleDownload}
              disabled={isDownloading}
              className={`w-full inline-flex items-center justify-center px-6 py-3 border border-transparent rounded-xl text-sm font-medium text-white ${
                isDownloading 
                  ? 'bg-gray-400 cursor-not-allowed' 
                  : 'bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700'
              } focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-200 ${
                isDarkMode ? 'focus:ring-offset-gray-800' : ''
              }`}
            >
              {isDownloading ? (
                <>
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Downloading...
                </>
              ) : (
                <>
                  <ArrowDownTrayIcon className="h-5 w-5 mr-2" />
                  Download CSV
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}; 